/** DealSkout semantic design tokens — navy / warm yellow. Avoid yellow body text on light surfaces. */

export const brandColors = {
  navy: '#0B1F3A',
  navyMid: '#163A5F',
  navySoft: '#1E4D7B',
  yellow: '#F5C518',
  yellowDark: '#B8860B',
} as const;

export type ThemeModePreference = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'dealskout.theme';

export const lightTokens = {
  mode: 'light' as const,
  brand: brandColors,
  background: {
    default: '#F4F6F9',
    paper: '#FFFFFF',
    raised: '#FFFFFF',
  },
  border: '#D5DCE6',
  text: {
    primary: '#0B1F3A',
    secondary: '#3D4F66',
    /** Helper copy only. Darkened so it still clears AA on white. */
    muted: '#5C6E84',
  },
  success: { main: '#1B7A4E', contrastText: '#FFFFFF' },
  /** Dark gold so warning text and white-on-chip both clear AA. Brand yellow stays for CTAs. */
  warning: { main: '#8A5600', contrastText: '#FFFFFF' },
  danger: { main: '#C62828', contrastText: '#FFFFFF' },
  info: { main: '#1565C0', contrastText: '#FFFFFF' },
  alert: {
    critical: { bg: '#FDECEC', text: '#7A1515', border: '#C62828' },
    attention: { bg: '#FFF3D6', text: '#6B3E00', border: '#C47D00' },
    caution: { bg: '#FFF8E8', text: '#5C4300', border: '#C9A227' },
    info: { bg: '#E8F1FB', text: '#0B1F3A', border: '#1565C0' },
    success: { bg: '#E6F6EC', text: '#0D5C32', border: '#1B7A4A' },
  },
  /** Accent for highlights/CTAs — not for body text on light surfaces */
  accent: brandColors.yellow,
  accentContrast: brandColors.navy,
};

export const darkTokens = {
  mode: 'dark' as const,
  brand: brandColors,
  background: {
    default: '#071525',
    paper: '#0F2744',
    raised: '#163A5F',
  },
  border: '#2A4566',
  text: {
    primary: '#F2F5F9',
    secondary: '#C5D0DE',
    muted: '#8FA3BB',
  },
  success: { main: '#4CAF7A', contrastText: '#071525' },
  warning: { main: '#F5C518', contrastText: '#0B1F3A' },
  /** Light red so error text clears the navy paper; chip label uses navy contrastText. */
  danger: { main: '#FF8A80', contrastText: '#0B1F3A' },
  info: { main: '#64B5F6', contrastText: '#071525' },
  alert: {
    critical: { bg: '#3F1717', text: '#FFD6D6', border: '#FF8A80' },
    attention: { bg: '#3A2E10', text: '#FFE7A8', border: '#F5C518' },
    caution: { bg: '#332C14', text: '#F3E2B0', border: '#E0B84A' },
    info: { bg: '#132C4A', text: '#E4F0FC', border: '#90CAF9' },
    success: { bg: '#123528', text: '#D7F5E4', border: '#81C784' },
  },
  accent: brandColors.yellow,
  accentContrast: brandColors.navy,
};

export type SemanticTokens = typeof lightTokens;
